/**
 * Italian Menu Template
 * Mediterranean-style menu design
 */
import React from "react";
import { FTU_LOGO_URL } from "../constants";

export function ItalianTemplate({ 
  menuRef,
  generatedMenu, 
  showPrix,
  customLogoUrl,
  pageOrientation = "portrait"
}) {
  if (!generatedMenu) return null;

  return (
    <div
      ref={menuRef}
      className="relative p-4"
      style={{
        width: pageOrientation === "landscape" ? "297mm" : "210mm",
        minHeight: pageOrientation === "landscape" ? "210mm" : "297mm",
        background: "linear-gradient(180deg, #1a472a 0%, #2d5a3d 50%, #8B0000 100%)",
      }}
    >
      {/* Italian flag stripe at top */}
      <div className="absolute top-0 left-0 right-0 h-3 flex">
        <div className="flex-1 bg-green-600" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-red-600" />
      </div>
      
      {/* Decorative border */}
      <div className="absolute inset-4 border-4 border-amber-100/30 rounded-xl" />
      
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col p-6 pt-8">
        {/* Header with Logo */}
        <div className="text-center mb-4">
          {customLogoUrl ? (
            <img 
              src={customLogoUrl} 
              alt="Logo"
              className="h-20 mx-auto mb-2 drop-shadow-lg"
            />
          ) : (
            <div className="text-5xl mb-2">🍕</div>
          )}
          <h1 className="text-3xl font-serif font-bold text-amber-100 drop-shadow-lg italic">
            {generatedMenu.titre || "La Carta"}
          </h1>
          {generatedMenu.sous_titre && (
            <p className="text-amber-200/80 text-sm italic">{generatedMenu.sous_titre}</p>
          )}
        </div>
        
        {/* Menu Grid */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 auto-rows-min">
          {generatedMenu.sections?.map((section, sIdx) => (
            <React.Fragment key={sIdx}>
              {/* Section Header */}
              <div className="col-span-2 md:col-span-3 mt-2 first:mt-0">
                <h2 className="text-lg font-serif font-bold text-amber-100 text-center py-1 bg-black/30 rounded-lg backdrop-blur-sm italic">
                  {section.type === "entree" ? "🥗 Antipasti" :
                   section.type === "plat" ? "🍝 Piatti" :
                   section.type === "dessert" ? "🍰 Dolci" : "🍷 Bevande"}
                </h2>
              </div>
              
              {/* Dishes */}
              {section.plats?.map((plat, pIdx) => (
                <div 
                  key={pIdx}
                  className="bg-amber-50/95 rounded-xl overflow-hidden shadow-lg border-2 border-amber-200"
                >
                  {/* Dish Photo */}
                  <div className="aspect-square bg-gradient-to-br from-amber-100 to-red-50 relative overflow-hidden">
                    {plat.image_url ? (
                      <img 
                        src={plat.image_url} 
                        alt={plat.nom}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">
                        {section.type === "entree" ? "🥗" :
                         section.type === "plat" ? "🍝" :
                         section.type === "dessert" ? "🍰" : "🍷"}
                      </div>
                    )}
                    
                    {/* Price Badge */}
                    {showPrix && (
                      <div className={`absolute top-2 right-2 ${plat.original_prix && plat.original_prix !== plat.prix ? 'bg-orange-600' : 'bg-red-700'} text-white font-bold px-2 py-1 rounded-full text-sm shadow-lg`}>
                        {plat.original_prix && plat.original_prix !== plat.prix && (
                          <span className="text-xs line-through opacity-60 mr-1">{plat.original_prix.toFixed(0)}€</span>
                        )}
                        {plat.prix.toFixed(0)} €
                      </div>
                    )}
                  </div>
                  
                  {/* Dish Info */}
                  <div className="p-2 text-center bg-gradient-to-b from-amber-50 to-amber-100">
                    <h3 className="font-serif font-bold text-red-900 text-sm leading-tight italic">{plat.nom}</h3>
                    {plat.description && (
                      <p className="text-xs text-amber-800 mt-0.5 line-clamp-2">{plat.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
        
        {/* Footer */}
        <div className="text-center mt-4 pt-3 border-t border-amber-100/30">
          {/* Logo Food Truck United */}
          <img 
            src={FTU_LOGO_URL} 
            alt="Food Truck United" 
            className="h-8 mx-auto mb-2 brightness-0 invert opacity-60"
          />
          <p className="text-amber-100/80 text-xs italic">
            {generatedMenu.food_truck?.nom} • Buon Appetito! 🇮🇹
          </p>
          <p className="text-amber-100/60 text-xs mt-1">
            Pour toutes allergies, merci de nous consulter
          </p>
        </div>
      </div>
    </div>
  );
}

export default ItalianTemplate;
